import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMountain,
  faCalendar,
  faVest,
} from "@fortawesome/free-solid-svg-icons";
import {
  wildernessFeatures,
  eventSchedule,
  mandatoryGearsSMM,
  getGearIcon,
  CheckCircle,
} from "@mmfestival/shared-components/data/SMMDetail";

export const SectionSMMDetail = ({ selectedCategory }) => {
  const highlights = wildernessFeatures;
  const scheduleItems = eventSchedule;

  // Menentukan kategori berdasarkan selectedCategory
  const rawCategoryName = selectedCategory?.categoryName || "Marathon";

  // Helper untuk normalisasi string (lowercase + hapus non-alphanum)
  const normalize = (s = "") =>
    String(s)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, "");

  // Cari key di mandatoryGearsSMM yang cocok dengan beberapa strategi
  const findMandatoryKey = (obj, categoryName) => {
    if (!obj || typeof obj !== "object") return null;
    if (!categoryName) return null;

    // 1. exact match
    if (obj[categoryName]) return categoryName;

    const keys = Object.keys(obj);
    if (keys.length === 0) return null;

    // 2. case-insensitive exact
    const lower = categoryName.toLowerCase().trim();
    const keyByLower = keys.find((k) => k.toLowerCase().trim() === lower);
    if (keyByLower) return keyByLower;

    // 3. normalized (hapus spasi/karakter)
    const norm = normalize(categoryName);
    const keyByNorm = keys.find((k) => normalize(k) === norm);
    if (keyByNorm) return keyByNorm;

    // 4. includes / partial match (category contains key or key contains category)
    const keyIncludes = keys.find(
      (k) =>
        k.toLowerCase().includes(lower) ||
        lower.includes(k.toLowerCase().trim())
    );
    if (keyIncludes) return keyIncludes;

    // no match
    return null;
  };

  // debug: tampilkan key yang tersedia / raw category supaya gampang diagnosa di console
  // (hapus/komentari setelah masalah teratasi)
  if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
    try {
      // eslint-disable-next-line no-console
      console.debug(
        "[SectionSMMDetail] mandatoryGearsSMM keys:",
        Object.keys(mandatoryGearsSMM || {})
      );
      // eslint-disable-next-line no-console
      console.debug(
        "[SectionSMMDetail] selectedCategory.categoryName:",
        rawCategoryName
      );
    } catch (e) {
      // ignore
    }
  }

  const matchedKey = findMandatoryKey(mandatoryGearsSMM, rawCategoryName);

  // If no direct match, attempt sensible fallback: 'Marathon' or first available key
  let effectiveKey = matchedKey;
  if (!effectiveKey) {
    if (mandatoryGearsSMM["Marathon"]) {
      effectiveKey = "Marathon";
    } else {
      const keys = Object.keys(mandatoryGearsSMM || {});
      effectiveKey = keys.length > 0 ? keys[0] : null;
    }
  }

  const categoryName = rawCategoryName;

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.4,
        ease: "easeInOut",
      },
    }),
  };

  const leftHighlights = highlights.slice(0, 2);
  const rightHighlights = highlights.slice(2, 4);

  return (
    <motion.div
      className="w-full bg-white py-12"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2, ease: "easeInOut" }}
      viewport={{ once: true }}
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Container Atas - Wilderness Features */}
        <motion.div
          className="bg-white mb-8"
          initial="hidden"
          whileInView="visible"
          variants={itemVariants}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-4">
            <FontAwesomeIcon
              icon={faMountain}
              className="text-lg text-green-600"
            />
            {/* Sesuaikan ukuran font: mobile lebih kecil, desktop lebih besar */}
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
              Wilderness Features
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              {leftHighlights.map((highlight, index) => (
                <motion.div
                  key={index}
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  variants={itemVariants}
                  viewport={{ once: true }}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full bg-green-600"></div>
                    </div>
                    <div className="flex-1">
                      {/* Ukuran judul highlight dibuat responsif */}
                      <h3 className="font-bold text-gray-900 mb-1 text-sm lg:text-base">
                        {highlight.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {highlight.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="space-y-4">
              {rightHighlights.map((highlight, index) => (
                <motion.div
                  key={index + 2}
                  custom={index + 2}
                  initial="hidden"
                  whileInView="visible"
                  variants={itemVariants}
                  viewport={{ once: true }}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full bg-green-600"></div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-1 text-sm lg:text-base">
                        {highlight.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {highlight.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Container Tengah - Event Schedule */}
        <motion.div
          className="bg-white mb-8"
          initial="hidden"
          whileInView="visible"
          variants={itemVariants}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-4">
            <FontAwesomeIcon
              icon={faCalendar}
              className="text-lg text-green-600"
            />
            {/* Ukuran font Event Schedule disesuaikan responsif */}
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
              Event Schedule
            </h2>
          </div>

          <div className="space-y-4">
            {scheduleItems.map((item, index) => (
              <motion.div
                key={index}
                custom={index}
                initial="hidden"
                whileInView="visible"
                variants={itemVariants}
                viewport={{ once: true }}
                className="bg-gray-100 rounded-lg p-4 flex justify-between items-start transition-colors duration-300"
              >
                <div className="flex-1">
                  {/* Judul event responsif */}
                  <h3 className="font-bold text-gray-900 mb-1 text-sm lg:text-base">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{item.date}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="font-semibold text-gray-900 text-sm">
                    {item.time}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Container Bawah - Mandatory Gears (HIDDEN to match BMM layout) */}
        {/* 
        <motion.div
          className="bg-white"
          initial="hidden"
          whileInView="visible"
          variants={itemVariants}
          viewport={{ once: true }}
        >
          ... (table content) ...
        </motion.div>
        */}

      </div>
    </motion.div>
  );
};
